import { useEffect, useState } from 'react';
import { Spin, Alert, Tag, Card, Space, Divider } from 'antd';
import { getPermissionsForInspection } from '../../../apiCalls/iqcAPIs';
import { getUserDetailsAPI } from '../../../../../common/apis/commonApis';

const getStatusTag = (status) => {
    const normalized = status?.toLowerCase();
    if (normalized === 'approved') return <Tag color="green">Approved</Tag>;
    if (normalized === 'rejected') return <Tag color="red">Rejected</Tag>;
    if (normalized === 'pending') return <Tag color="orange">Pending</Tag>;
    return <Tag>{status}</Tag>;
};

export function PermissionTab({ inspectionDetails }) {
    const [permissions, setPermissions] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!inspectionDetails?.id) return;

        const fetchPermissionsAndUsers = async () => {
            try {
                setLoading(true);
                const response = await getPermissionsForInspection(inspectionDetails.id);
                const perms = response?.data || [];
                setPermissions(perms);

                // Extract unique user IDs from steps
                const userIds = new Set();
                perms.forEach((perm) =>
                    perm.approvalSteps?.forEach((step) =>
                        step.approverIds?.forEach((id) => userIds.add(id))
                    )
                );

                // Fetch user names once per ID
                const idToName = {};
                await Promise.all(
                    Array.from(userIds).map(async (id) => {
                        try {
                            const user = await getUserDetailsAPI(id);
                            idToName[id] = user?.data?.name || id;
                        } catch {
                            idToName[id] = id;
                        }
                    })
                );

                setUserMap(idToName);
                setError('');
            } catch (err) {
                console.error('Permission fetch failed:', err);
                setError('Failed to load permissions');
            } finally {
                setLoading(false);
            }
        };

        fetchPermissionsAndUsers();
    }, [inspectionDetails?.id]);

    if (!loading && !error && permissions.length === 0) return null;

    return (
        <div
            style={{
                border: '1px solid #ddd',
                padding: '16px',
                borderRadius: '8px',
                background: '#fafafa',
            }}
        >
            <span style={{ fontWeight: 'bold' }}>Approvals</span>

            {loading && <Spin />}
            {error && <Alert type="error" message={error} showIcon />}

            {!loading && !error && permissions.length > 0 && (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {permissions.map((perm) => (
                        <Card
                            key={perm.id}
                            size="small"
                            title={`Approval Request: ${perm.id.slice(0, 6)}...`}
                        >
                            <div>
                                <strong>Status:</strong> {getStatusTag(perm.status)}
                            </div>
                            <Divider style={{ margin: '8px 0' }} />

                            {perm.approvalSteps?.length > 0 ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {[...perm.approvalSteps]
                                        .sort((a, b) => a.stepNumber - b.stepNumber)
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                style={{
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: 4,
                                                    padding: '8px',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <div>
                                                        <strong>Step {step.stepNumber}</strong> —{' '}
                                                        {step.approverIds?.length > 0
                                                            ? `Approvers: ${step.approverIds
                                                                .map((id) => userMap[id] || id)
                                                                .join(', ')}`
                                                            : 'No approvers'}
                                                    </div>
                                                    <div>{getStatusTag(step.status)}</div>
                                                </div>

                                                {(step.remarks || step.decisionAt) && (
                                                    <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                                                        {step.remarks && (
                                                            <div>
                                                                <strong>Remark:</strong> {step.remarks}
                                                            </div>
                                                        )}
                                                        {step.decisionAt && (
                                                            <div>
                                                                <strong>Decision At:</strong>{' '}
                                                                {new Date(step.decisionAt).toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                </Space>
                            ) : (
                                <div style={{ fontStyle: 'italic', color: '#888' }}>
                                    No approval steps found
                                </div>
                            )}
                        </Card>
                    ))}
                </Space>
            )}
        </div>
    );
}
